import { useMemo, useState } from 'react'
import {
  AppBar, Box, Dialog, IconButton, Toolbar, Tooltip, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import MenuOpenIcon from '@mui/icons-material/MenuOpen'
import MenuIcon from '@mui/icons-material/Menu'
import { useSelector } from 'react-redux'
import { selectAdminConfigs } from '../../reducers/Beacon4PerformanceNetworkOverviewAdminReducer'
import Beacon4PerformanceNetworkOverviewAdminKpiList from './Beacon4PerformanceNetworkOverviewAdminKpiList'
import Beacon4PerformanceNetworkOverviewAdminKpiDetail from './Beacon4PerformanceNetworkOverviewAdminKpiDetail'
import Beacon4PerformanceNetworkOverviewAdminMetadataDialog from './Beacon4PerformanceNetworkOverviewAdminMetadataDialog'

/**
 * Manage KPIs — master/detail layout (list on the left, KPI detail on the
 * right). The selection drives a single context, so every sub-tab
 * (Overview / Formula / Breakdowns / Audit) is scoped to the same KPI.
 */
export default function Beacon4PerformanceNetworkOverviewAdminDialog({ open, onClose }) {
  const configs = useSelector(selectAdminConfigs)

  const [selectedId, setSelectedId] = useState(null)
  const [metaOpen, setMetaOpen]     = useState(false)
  const [metaConfig, setMetaConfig] = useState(null) // null = create/clone
  const [cloneSource, setCloneSource] = useState(null) // set only for clone
  const [listOpen, setListOpen]     = useState(true)
  // Id of a KPI that should open straight on the Formula tab. Set right after a
  // create, because the New KPI dialog no longer collects the formula — the
  // Formula tab is where that happens, and dropping the user on Overview would
  // leave them looking at a KPI with no data source and no hint of what's next.
  const [formulaFor, setFormulaFor] = useState(null)

  // Derive the selected config from the store so the detail pane stays fresh
  // after edits / refetches.
  const selectedConfig = useMemo(
    () => configs.find(c => c.id === selectedId) ?? null,
    [configs, selectedId]
  )

  // Picking from the list is always a plain selection: clear the Formula-tab
  // request so it can't reopen on a later, unrelated selection.
  const onSelect = (id) => { setSelectedId(id); setFormulaFor(null) }

  const openCreate = () => { setMetaConfig(null); setCloneSource(null); setMetaOpen(true) }
  const openEdit   = (cfg) => { setMetaConfig(cfg); setCloneSource(null); setMetaOpen(true) }
  const openClone  = (cfg) => { setMetaConfig(null); setCloneSource(cfg); setMetaOpen(true) }
  const closeMeta  = () => { setMetaOpen(false); setCloneSource(null) }

  // Called by the metadata dialog after a successful save. On create it hands
  // back the new id so we can select it and jump to Formula; on edit there is
  // no id and the selection is already right.
  const onMetaSaved = ({ id } = {}) => {
    if (id == null) return
    setSelectedId(id)
    setFormulaFor(id)
    setListOpen(true)
  }

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar position="relative" elevation={0} sx={{ bgcolor: 'primary.main' }}>
        <Toolbar variant="dense">
          <Tooltip title={listOpen ? 'Hide KPI list' : 'Show KPI list'}>
            <IconButton
              size="small" onClick={() => setListOpen(o => !o)}
              sx={{ color: '#fff', mr: 1 }}
              aria-label={listOpen ? 'Hide KPI list' : 'Show KPI list'}
            >
              {listOpen ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </Tooltip>
          <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
            Network Overview — Manage KPIs
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', bgcolor: 'background.default' }}>
        {/* Unmounted rather than hidden when collapsed: the list owns the
            fetchAdminConfigs on mount, and keeping it in the tree with width 0
            would leave a focusable, scrollable pane the user can tab into. The
            configs already live in the store, so remounting costs one refetch. */}
        {listOpen && (
          <Beacon4PerformanceNetworkOverviewAdminKpiList
            selectedId={selectedId}
            onSelect={onSelect}
            onNew={openCreate}
          />
        )}
        <Beacon4PerformanceNetworkOverviewAdminKpiDetail
          config={selectedConfig}
          onEditMetadata={openEdit}
          onClone={openClone}
          openOnFormulaFor={formulaFor}
        />
      </Box>

      <Beacon4PerformanceNetworkOverviewAdminMetadataDialog
        open={metaOpen}
        config={metaConfig}
        cloneSource={cloneSource}
        onClose={closeMeta}
        onSaved={onMetaSaved}
      />
    </Dialog>
  )
}